import { Injectable } from "@angular/core";
import { IAccountBasedData, IAppData, IPushSubscription, StartupData } from "@app/models";
import { BehaviorSubject, Observable, Subject, takeUntil } from "rxjs";
import { AccountsService } from "../app-services/accounts.service";
import { ConfigService } from "../api/config.service";
import { AuthService } from "@app/auth/auth.service";
import { IOutOfHomeActivity } from "@app/models/out-of-home-activity";

@Injectable({
    providedIn: 'root',
})
export class AppDataService {
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    private _appData: BehaviorSubject<IAppData | null> = new BehaviorSubject(null);
    private _currentAppData: IAppData = {} as IAppData;
    private _isLoadingAccountData: BehaviorSubject<boolean> = new BehaviorSubject(false);

    constructor(
        private _accountsService: AccountsService,
        private _configService: ConfigService,
        private _authService: AuthService,
        ) {
            if (this._authService.isAuthUserLoggedIn()) {
                this._accountsService.getAccountId().pipe(takeUntil(this._unsubscribeAll)).subscribe((accountId) =>
                { //do get account based data whenever there is a (non-empty) account to fetch for
                    if (!!accountId) {
                        this._isLoadingAccountData.next(true);
                        this._configService.accountBasedDataReadOne(accountId).subscribe({
                            next: (data: IAccountBasedData) => this.updateAccountBasedData(data),
                            complete: () => this._isLoadingAccountData.next(false),
                            error: () => this._isLoadingAccountData.next(false),
                        })
                    }
                });
            }
        }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for app data
     */
    get appData$(): Observable<IAppData> {
        return this._appData.asObservable();
    }

    /**
     * Getter for the account data loading state, emitted whenever the selected account changes
     */
    get isLoadingAccountData$(): Observable<boolean> {
        return this._isLoadingAccountData.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * adds the startup data to the app data
     * @param data startup data
     */
    setStartupData(startupData: IAppData): void {
        this._currentAppData = startupData;
        this._appData.next(startupData);
    }

    /**
     * updates the account based in the app data
     * @param accountData account based data
     */
    updateAccountBasedData(accountData: IAccountBasedData): void {
        this._currentAppData.AccountBasedData = accountData;        
        this._appData.next(this._currentAppData);
    }


    /**
     * Updates the subscriber out-of-home activities
     * @param subscriberId The subscriber identifier
     * @param activities The out-of-home activities
     */
    updateSubscriberOutOfHomeActivities(subscriberId: string, activities: IOutOfHomeActivity[]): void {
        const subscriber = this._currentAppData?.AccountBasedData?.Subscribers?.find(s => s.Id === subscriberId);
        if (subscriber) {
            subscriber.OutOfHomeActivities = activities;
            this._appData.next(this._currentAppData);
        }
    }
    
    /**
     * updates the push notifications in the app data
     * @param notifData notifications data
     */
    updateNotificationsData(notifData: IPushSubscription[]): void {
        this._currentAppData.UserData.NotificationSubscriptions = notifData;
        this._appData.next(this._currentAppData);
    }
}
